/**
 * The Custom SQL ↔ Sofia protocol: builds the seed prompt handed to Sofia when a
 * user clicks "Ask Sofia" (teaching it the query contract, the available schema,
 * and how to recommend settings), and parses Sofia's reply back into a SQL query
 * plus optional aggregation-method / unit recommendations the form can apply.
 */

import { extractSqlFromMarkdown } from "@/lib/coviSqlHandoff"

import {
  resolveAggregationMethod,
  resolveUnitToken,
  SQL_AGGREGATION_METHOD_OPTIONS,
  SQL_UNIT_OPTIONS,
  type SqlAggregationMethod,
} from "./sqlMetricOptions"

interface PromptSchemaColumn {
  name: string
  sql_type?: string
  enum?: { name: string; values: readonly string[] } | null
}

interface PromptSchemaTable {
  name: string
  columns: readonly PromptSchemaColumn[]
}

interface BuildSqlMetricPromptArgs {
  /** Data-frame tables available to the query (from getSimulationDataFramesSchema). */
  tables: readonly PromptSchemaTable[]
}

function formatColumn(column: PromptSchemaColumn): string {
  const type = column.sql_type ? ` ${column.sql_type}` : ""
  const enumValues =
    column.enum && column.enum.values.length > 0
      ? ` [one of: ${column.enum.values.join(", ")}]`
      : ""
  return `${column.name}${type}${enumValues}`
}

function formatSchema(tables: readonly PromptSchemaTable[]): string {
  if (tables.length === 0) {
    return "(The data-frame schema isn't loaded yet — ask me to open the schema panel, or infer sensible table/column names and note the assumptions.)"
  }
  return tables
    .map(
      (table) =>
        `- ${table.name}(${table.columns.map(formatColumn).join(", ")})`,
    )
    .join("\n")
}

const AGGREGATION_CHOICES = SQL_AGGREGATION_METHOD_OPTIONS.map(
  (option) => option.key,
).join(", ")

const UNIT_CHOICES = SQL_UNIT_OPTIONS.map((option) => option.key)
  .filter((key) => key !== "none")
  .join(", ")

const QUERY_CONTRACT = [
  "- Return one row per data point (not the aggregate).",
  "- Include a numeric `value` column — alias your expression `AS value`.",
  "- Include a `start_offset_milliseconds` column; `end_offset_milliseconds` is optional.",
  "- Do not select any other columns.",
  "- An aggregation method reduces the `value` column to the metric's single result.",
].join("\n")

export function buildSqlMetricPrompt({
  tables,
}: BuildSqlMetricPromptArgs): string {
  return [
    "I want to create a \"Custom SQL\" metric in Coval, but I haven't decided exactly what to measure yet. Ask me what I'd like the metric to measure, then help me build it.",
    "",
    "The query runs over a single simulation's structured data frames and is reduced to one numeric value. Use generic SQL, avoiding database-specific syntax and functions.",
    "",
    "Query contract:",
    QUERY_CONTRACT,
    "",
    "Available tables and columns:",
    formatSchema(tables),
    "",
    "Once you know what I want:",
    "1. Give the query in a ```sql code block so I can add it to the editor.",
    "2. After the query, add these lines exactly so I can apply them automatically:",
    "   Suggested name: <a short, human-readable metric name>",
    "   Suggested description: <one sentence describing what the metric measures>",
    "   Suggested settings — aggregation: <method>, unit: <unit>",
    `   where <method> is one of: ${AGGREGATION_CHOICES}; and <unit> is one of: ${UNIT_CHOICES}, or none for unitless.`,
  ].join("\n")
}

interface CoviSqlReply {
  /** The SQL query from the reply's code block, or null if none. */
  sql: string | null
  /** Suggested metric name, or null if none. */
  name: string | null
  /** Suggested metric description, or null if none. */
  description: string | null
  /** Recommended aggregation method, or null if not recommended/unrecognized. */
  aggregationMethod: SqlAggregationMethod | null
  /** Recommended unit (metadataUnit undefined = unitless), or null if none. */
  unit: { metadataUnit: string | undefined } | null
}

/** Pulls the value from a "Suggested <label>: <value>" line (markdown-tolerant). */
function extractSuggestion(message: string, label: string): string | null {
  const match = new RegExp(
    `^[*_\\s>-]*suggested ${label}\\s*[-–—:][*_\\s]*(.+?)[*_\\s]*$`,
    "im",
  ).exec(message)
  const value = match?.[1]?.trim().replace(/^["'`]+|["'`]+$/g, "")
  return value && value.length > 0 ? value : null
}

/**
 * Parses Sofia's reply into the SQL query plus any suggested name, description,
 * and "Suggested settings" line it appended. Everything but the SQL is
 * best-effort: the SQL applies regardless, and anything missing/unrecognized is
 * simply left unapplied.
 */
export function parseCoviSqlReply(message: string): CoviSqlReply {
  const sql = extractSqlFromMarkdown(message)
  const name = extractSuggestion(message, "name")
  const description = extractSuggestion(message, "description")

  // The recommendation lives on a single line mentioning both fields; scoping to
  // that line avoids picking up "unit"/"count" tokens from the SQL itself.
  const settingsLine = message
    .split("\n")
    .map((line) => line.trim())
    .find((line) => /aggregation/i.test(line) && /unit/i.test(line))

  let aggregationMethod: SqlAggregationMethod | null = null
  let unit: { metadataUnit: string | undefined } | null = null

  if (settingsLine) {
    const aggregationMatch =
      /aggregation(?:\s+method)?\s*[:=]\s*`?([a-zA-Z_]+)`?/i.exec(settingsLine)
    if (aggregationMatch) {
      aggregationMethod = resolveAggregationMethod(aggregationMatch[1])
    }
    const unitMatch = /\bunit\s*[:=]\s*`?([a-zA-Z_]+)`?/i.exec(settingsLine)
    if (unitMatch) {
      unit = resolveUnitToken(unitMatch[1])
    }
  }

  return { sql, name, description, aggregationMethod, unit }
}
